/**
 * API Key Service
 *
 * Service for API key CRUD operations in the admin interface.
 * Matches backend controller: WiCloud.Admin.API/Controllers/ApiKeyController.
 */

import { apiClient } from '@/lib/api'
import type { StandardResponse, MessageResponse } from '@/types/api-response'
import { isErrorResponse, extractData } from '@/types/api-response'
import type {
  ApiKey,
  ApiKeyListItem,
  CreateApiKeyRequest,
  CreateApiKeyResult,
} from '@/types/api-keys.types'

export const adminApiKeyService = {
  /**
   * Get list of all API keys
   *
   * GET /api/v1/api-keys
   *
   * @returns Array of API key list items
   */
  async listApiKeys(): Promise<ApiKeyListItem[]> {
    const response = await apiClient.get<StandardResponse<ApiKeyListItem[]>>('/api/v1/api-keys')

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message)
    }

    return extractData(response.data)
  },

  /**
   * Create a new API key
   *
   * POST /api/v1/api-keys
   *
   * @param request - Create API key request
   * @returns Created API key with plain text key (shown only once)
   */
  async createApiKey(request: CreateApiKeyRequest): Promise<CreateApiKeyResult> {
    const response = await apiClient.post<StandardResponse<CreateApiKeyResult>>(
      '/api/v1/api-keys',
      request
    )

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message)
    }

    return extractData(response.data)
  },

  /**
   * Delete an API key
   *
   * DELETE /api/v1/api-keys/{id}
   *
   * @param id - API key ID
   */
  async deleteApiKey(id: string): Promise<void> {
    const response = await apiClient.delete<MessageResponse>(`/api/v1/api-keys/${id}`)

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message)
    }
  },

  /**
   * Revoke an API key
   *
   * POST /api/v1/api-keys/{id}/revoke
   *
   * @param id - API key ID
   */
  async revokeApiKey(id: string): Promise<void> {
    const response = await apiClient.post<MessageResponse>(`/api/v1/api-keys/${id}/revoke`)

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message)
    }
  },
}
